import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import { createTooltipProps } from '../utils/fieldTooltips';
import MissingSelectOption from '../components/MissingSelectOption';
import { ValidationRules, validateField } from '../utils/validation';
import {
  appendCategoryNameBulkUpdateFlag,
  categoryNameUpdateSuccessMessage,
  maybeConfirmCategoryNameBulkUpdate,
} from '../utils/categoryNameBulkUpdate';

function SalesItemModal({ salesItem, onClose, onSave, defaultCategory = '' }) {
  const [formData, setFormData] = useState({
    item_code: '',
    item_name: '',
    category: defaultCategory,
    unit_cost: undefined,
    sale_price: undefined,
    stock_quantity: '',
    reorder_level: '',
    supplier: '',
    active: true,
  });

  const [salesCategories, setSalesCategories] = useState([]);
  const [modalError, setModalError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const fetchSalesCategories = async () => {
    try {
      console.log('Fetching sales categories...');
      const response = await axios.get('/api/sales-categories/dropdown');
      console.log('Sales categories response:', response.data);
      setSalesCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch sales categories:', error);
    }
  };

  useEffect(() => {
    if (salesItem) {
      // Convert 0 values to undefined for financial fields
      const cleanedData = { ...salesItem, active: salesItem.active === 1 };
      const financialFields = ['unit_cost', 'sale_price'];
      financialFields.forEach((field) => {
        if (
          cleanedData[field] === 0 ||
          cleanedData[field] === '0' ||
          cleanedData[field] === '0.00'
        ) {
          cleanedData[field] = undefined;
        }
      });
      setFormData(cleanedData);
    } else {
      // Reset form for new item with default category
      setFormData((prev) => ({ ...prev, category: defaultCategory }));
    }
    fetchSalesCategories();
  }, [salesItem, defaultCategory]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError(''); // Clear any previous errors

    // Validate financial fields
    const errors = {};
    const financialFields = ['unit_cost', 'sale_price'];

    financialFields.forEach((field) => {
      if (formData[field] && formData[field] !== '') {
        const error = validateField(
          field,
          formData[field],
          ValidationRules.salesItem[field],
          formData
        );
        if (error) {
          errors[field] = error;
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setModalError('Please fix the validation errors before saving.');
      return;
    }

    setValidationErrors({});

    try {
      await onSave(formData);
    } catch (error) {
      setModalError(error.response?.data?.error || 'Failed to save sales item');
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>{salesItem ? 'Edit Sales Item' : 'Add New Sales Item'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {modalError && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {modalError}
              <button
                type="button"
                className="btn-close"
                onClick={() => setModalError('')}
                aria-label="Close"
              ></button>
            </div>
          )}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Item Code *</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('item_code')}>
                      Unique identifier for the sales item
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="text"
                    name="item_code"
                    value={formData.item_code}
                    onChange={handleChange}
                    required
                    {...createTooltipProps('item_code')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Item Name *</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('item_name')}>
                      Used in user interfaces, reports, and equipment selection
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="text"
                    name="item_name"
                    value={formData.item_name}
                    onChange={handleChange}
                    required
                    {...createTooltipProps('item_name')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('category')}>
                      Used in equipment classification, pricing, and sales contract management
                    </Tooltip>
                  }
                >
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    {...createTooltipProps('category')}
                  >
                    <option value="">Select a category...</option>
                    <MissingSelectOption
                      value={formData.category}
                      options={salesCategories}
                      getOptionValue={(category) => category.code}
                    />
                    {console.log('Rendering salesCategories:', salesCategories)}
                    {salesCategories.map((category) => (
                      <option key={category.id} value={category.code}>
                        {category.code} - {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Supplier</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('supplier')}>
                      Equipment supplier or manufacturer
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="text"
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleChange}
                    {...createTooltipProps('supplier')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Unit Cost</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('unit_cost')}>
                      Cost price for purchasing the item
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="number"
                    name="unit_cost"
                    value={formData.unit_cost}
                    onChange={handleChange}
                    step="0.01"
                    isInvalid={!!validationErrors.unit_cost}
                    {...createTooltipProps('unit_cost')}
                  />
                </OverlayTrigger>
                {validationErrors.unit_cost && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {validationErrors.unit_cost}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sale Price</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('sale_price')}>
                      Selling price for the item
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="number"
                    name="sale_price"
                    value={formData.sale_price}
                    onChange={handleChange}
                    step="0.01"
                    isInvalid={!!validationErrors.sale_price}
                    {...createTooltipProps('sale_price')}
                  />
                </OverlayTrigger>
                {validationErrors.sale_price && (
                  <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {validationErrors.sale_price}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Stock Quantity</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('stock_quantity')}>
                      Current quantity in stock
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="number"
                    name="stock_quantity"
                    value={formData.stock_quantity}
                    onChange={handleChange}
                    {...createTooltipProps('stock_quantity')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Reorder Level</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('reorder_level')}>
                      Minimum stock level before reordering
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="number"
                    name="reorder_level"
                    value={formData.reorder_level}
                    onChange={handleChange}
                    {...createTooltipProps('reorder_level')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip {...createTooltipProps('active')}>
                  Controls whether the item is active and available for sale
                </Tooltip>
              }
            >
              <Form.Check
                type="checkbox"
                name="active"
                label="Active"
                checked={formData.active}
                onChange={handleChange}
                {...createTooltipProps('active')}
              />
            </OverlayTrigger>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {salesItem ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function SalesItems() {
  const [salesItems, setSalesItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSalesItem, setEditingSalesItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();
  const [salesCategories, setSalesCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchSalesItems = async (page = 1, search = '', category = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/sales-items', {
        params: {
          page,
          limit: pagination.limit,
          search,
          category,
        },
      });
      setSalesItems(response.data.salesItems);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch sales items');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesCategories = async () => {
    try {
      const response = await axios.get('/api/sales-categories?limit=1000');
      setSalesCategories(response.data.salesCategories || []);
    } catch (error) {
      console.error('Error fetching sales categories:', error);
    }
  };

  useEffect(() => {
    fetchSalesItems(1, '', selectedCategory);
    fetchSalesCategories();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSalesItems(1, searchTerm, selectedCategory);
  };

  const handlePageChange = (newPage) => {
    fetchSalesItems(newPage, searchTerm, selectedCategory);
  };

  const handleCategoryFilter = (categoryCode) => {
    setSelectedCategory(categoryCode);
    fetchSalesItems(1, searchTerm, categoryCode);
  };

  const handleAddSalesItem = () => {
    setEditingSalesItem(null);
    setShowModal(true);
  };

  const handleEditSalesItem = (salesItem) => {
    setEditingSalesItem(salesItem);
    setShowModal(true);
  };

  const handleSaveSalesItem = async (salesItemData) => {
    try {
      if (editingSalesItem) {
        const updateMatches = await maybeConfirmCategoryNameBulkUpdate(axios, {
          matchUrl: '/api/sales-items/name-matches',
          category: salesItemData.category || editingSalesItem.category,
          oldName: editingSalesItem.item_name,
          newName: salesItemData.item_name,
          excludeId: editingSalesItem.id,
          entityLabel: 'sales items',
        });
        const payload = appendCategoryNameBulkUpdateFlag(salesItemData, updateMatches);
        const response = await axios.put(`/api/sales-items/${editingSalesItem.id}`, payload);
        let successMessage = categoryNameUpdateSuccessMessage(
          'Sales item updated successfully',
          response.data.categoryNameUpdates
        );

        // Check if cascade updates occurred
        if (response.data.cascadeUpdates && response.data.cascadeUpdates.inclusions > 0) {
          const { inclusions } = response.data.cascadeUpdates;
          successMessage += `. Also updated ${inclusions} inclusion${inclusions !== 1 ? 's' : ''} with the new item code.`;
        }

        setSuccess(successMessage);
      } else {
        await axios.post('/api/sales-items', salesItemData);
        setSuccess('Sales item created successfully');
      }
      setShowModal(false);
      fetchSalesItems(pagination.page, searchTerm, selectedCategory);
    } catch (error) {
      // Re-throw the error so the modal can handle it
      throw error;
    }
  };

  const handleDeleteSalesItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this sales item?')) {
      try {
        await axios.delete(`/api/sales-items/${id}`);
        setSuccess('Sales item deleted successfully');
        fetchSalesItems(pagination.page, searchTerm, selectedCategory);
      } catch (error) {
        setError('Failed to delete sales item');
      }
    }
  };

  const handleDeleteAllSalesItems = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL sales items? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your sales item records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/sales-items');
          setSuccess(
            `All sales items deleted successfully (${response.data.deletedCount} records)`
          );
          setSelectedCategory('');
          fetchSalesItems(1, '', '');
        } catch (error) {
          setError('Failed to delete all sales items');
        }
      }
    }
  };

  const handleCopySalesItem = async (salesItem) => {
    setCopyingId(salesItem.id);
    try {
      const copiedData = { ...salesItem };
      delete copiedData.id;
      delete copiedData.created_at;
      delete copiedData.updated_at;

      // Smart increment: Strip trailing number and find highest existing number
      const originalCode = copiedData.item_code;
      const match = originalCode.match(/^(.*?)(\d+)$/);
      const baseCode = match ? match[1] : originalCode;
      const originalPadLength = match ? match[2].length : 2; // Preserve zero-padding

      // Find all existing codes with this base
      const existingNumbers = salesItems
        .map((item) => {
          const itemMatch = item.item_code.match(
            new RegExp(`^${baseCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`)
          );
          return itemMatch ? parseInt(itemMatch[1]) : 0;
        })
        .filter((num) => num > 0);

      // Get the highest number and increment
      const highestNum = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
      const newNumber = highestNum + 1;
      const newItemCode = `${baseCode}${String(newNumber).padStart(originalPadLength, '0')}`;

      copiedData.item_code = newItemCode;

      const token = localStorage.getItem('token');
      await wrapCopy('Copying sales item...', 1, async (tick) => {
        await axios.post('/api/sales-items', copiedData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        tick({ success: true });
      });

      setSuccess('Sales item copied successfully');
      fetchSalesItems(pagination.page, searchTerm, selectedCategory);
    } catch (error) {
      setError('Failed to copy sales item');
    } finally {
      setCopyingId(null);
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i
              className="fas fa-store me-3 text-purple"
              style={{ fontSize: '2.5rem', color: '#6f42c1' }}
            ></i>
            <div>
              <h1 className="mb-0">Sales Items</h1>
              <p className="text-muted mb-0">Manage individual products and services for sale</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button variant="primary" onClick={handleAddSalesItem} className="me-2">
            Add Sales Item
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllSalesItems}
            disabled={salesItems.length === 0}
          >
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {copyProgress && (
        <Row className="mb-3">
          <Col>
            <OperationProgress
              title={copyProgress.title}
              processed={copyProgress.processed}
              total={copyProgress.total}
              success={copyProgress.success}
              failed={copyProgress.failed}
              variant="info"
            />
          </Col>
        </Row>
      )}

      <Row>
        {/* Category Filter Sidebar */}
        {salesCategories.length > 0 && (
          <Col md={2} className="pe-0">
            <Card className="h-100">
              <Card.Header className="py-2">
                <small className="fw-bold">Categories</small>
              </Card.Header>
              <div
                className="list-group list-group-flush thin-scrollbar"
                style={{
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#ccc transparent',
                }}
              >
                <button
                  type="button"
                  className={`list-group-item list-group-item-action py-2 ${selectedCategory === '' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter('')}
                >
                  <small>All Categories</small>
                </button>
                {salesCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={`list-group-item list-group-item-action py-2 ${selectedCategory === category.code ? 'active' : ''}`}
                    onClick={() => handleCategoryFilter(category.code)}
                  >
                    <small>{category.name}</small>
                  </button>
                ))}
              </div>
            </Card>
          </Col>
        )}

        <Col md={salesCategories.length > 0 ? 10 : 12}>
          <Card>
            <Card.Header>
              <Form onSubmit={handleSearch} className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Search by item code, name, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-grow-1"
                />
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Form>
            </Card.Header>

            <Card.Body className="p-0">
              {loading ? (
                <div className="loading">Loading sales items...</div>
              ) : salesItems.length === 0 ? (
                <div className="empty-state">
                  <p>No sales items found</p>
                </div>
              ) : (
                <>
                  <Table responsive hover style={{ paddingLeft: '20px' }}>
                    <thead>
                      <tr>
                        <th style={{ paddingLeft: '30px' }}>Category</th>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th>Unit Cost</th>
                        <th>Sale Price</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesItems.map((item) => (
                        <tr key={item.id}>
                          <td style={{ paddingLeft: '30px' }}>{item.category}</td>
                          <td>{item.item_code}</td>
                          <td>
                            <strong>{item.item_name}</strong>
                          </td>
                          <td>£{item.unit_cost}</td>
                          <td>£{item.sale_price}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditSalesItem(item)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => handleCopySalesItem(item)}
                                disabled={copyingId === item.id}
                              >
                                {copyingId === item.id ? 'Copying...' : 'Copy'}
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteSalesItem(item.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <GridPagination
                    page={pagination.page}
                    pages={pagination.pages}
                    onPageChange={handlePageChange}
                    wrapperClassName="d-flex justify-content-center p-3"
                    ulClassName="mb-0"
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showModal && (
        <SalesItemModal
          salesItem={editingSalesItem}
          onClose={() => setShowModal(false)}
          onSave={handleSaveSalesItem}
          defaultCategory={selectedCategory}
        />
      )}
    </Container>
  );
}

export default SalesItems;
